import { ReducerCreators, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';

import { ContentState, UploadJob } from './types';
import { DeleteDocuments, GetDocuments } from '@/connection/content/documents';
import {
  DeleteVideos,
  GetPendingDownloadRequests,
  GetRecordedVideos,
  GetUploadedVideos,
} from '@/connection/content/videos';
import {
  GetProducts,
  DeleteProductChildren,
  PromoteProductChildren,
  UpdateProduct,
} from '@/connection/content/products';
import { Product } from '@/types/module.types';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import { UPLOAD_TYPES } from '@/micros/homepage/content/utils/content-utils';
import { GetTemplates } from '@/connection/templates';
import { GetFonts } from '@/connection/content/fonts';
import { DeleteColors, GetThemePacks } from '@/connection/theme-packs';
import { RootState } from '@/store/main';
import { cannotDeleteToast } from './utils';
import { StaticVideo } from '@/types/video.types';

export const reducers = (create: ReducerCreators<ContentState>) => ({
  loadProducts: create.asyncThunk(
    async (channel: number) => {
      return await GetProducts(channel);
    },
    {
      pending: (state) => {
        state.loadingContent = true;
      },
      fulfilled: (state, action) => {
        state.products = action.payload;
      },
      settled: (state) => {
        state.loadingContent = false;
      },
    }
  ),
  deleteProduct: create.asyncThunk(
    async (product: Product) => {
      if (!product.module_id) throw new Error('module_id required');
      if (!product.event_id) throw new Error('event_id required');

      return await DeleteProductChildren([product.module_id], product.event_id);
    },
    {
      fulfilled: (state, action) => {
        if (state.products) {
          state.products = state.products.filter((product) => product.product !== action.payload.products[0].product);
        }
      },
    }
  ),
  promoteProduct: create.asyncThunk(
    async ({ channel, product }: { channel: number; product: Product }) => {
      if (!product.module_id) throw new Error('module_id required');

      return await PromoteProductChildren(channel, [product.module_id]);
    },
    {
      pending: (state) => {
        state.loadingContent = true;
      },
      fulfilled: (state, action) => {
        if (state.products) {
          state.products = [...action.payload.products, ...state.products];
        }
      },
      settled: (state) => {
        state.loadingContent = false;
      },
    }
  ),
  updateProduct: create.asyncThunk(
    async ({ channel, product }: { channel: number; product: Product }) => {
      return await UpdateProduct(channel, product);
    },
    {
      fulfilled: (state, action) => {
        if (state.products) {
          const newProduct = action.payload.products[0];
          state.products = state.products.map((product) =>
            product.product === newProduct.product ? newProduct : product
          );
        }
      },
    }
  ),
  loadDocuments: create.asyncThunk(
    async (channel: number) => {
      return await GetDocuments(channel);
    },
    {
      pending: (state) => {
        state.loadingContent = true;
      },
      fulfilled: (state, action) => {
        state.documents = action.payload;
      },
      settled: (state) => {
        state.loadingContent = false;
      },
    }
  ),
  loadUploadedVideos: create.asyncThunk(
    async (channel: number) => {
      return await GetUploadedVideos(channel);
    },
    {
      pending: (state) => {
        state.loadingContent = true;
      },
      fulfilled: (state, action) => {
        state.uploadedVideos = action.payload;
      },
      settled: (state) => {
        state.loadingContent = false;
      },
    }
  ),
  loadRecordings: create.asyncThunk(
    async (channel: number) => {
      return await GetRecordedVideos(channel);
    },
    {
      pending: (state) => {
        state.loadingContent = true;
      },
      fulfilled: (state, action) => {
        state.recordings = action.payload;
      },
      settled: (state) => {
        state.loadingContent = false;
      },
    }
  ),
  loadPendingDownloads: create.asyncThunk(
    async (channel: number) => {
      return await GetPendingDownloadRequests(channel);
    },
    {
      pending: (state) => {
        state.loadingContent = true;
      },
      fulfilled: (state, action) => {
        state.pendingDownloads = action.payload;
      },
      settled: (state) => {
        state.loadingContent = false;
      },
    }
  ),
  setViewType: create.reducer((state, action: PayloadAction<'grid' | 'list'>) => {
    state.viewType = action.payload;
  }),
  setSearch: create.reducer((state, action: PayloadAction<string>) => {
    state.search = action.payload;
    state.pageNumber = 1;
  }),
  setSortBy: create.reducer((state, action: PayloadAction<string>) => {
    state.sortBy = action.payload;
  }),
  updateContentMarkedForDeletion: create.reducer(
    (state, action: PayloadAction<{ id: string | number; action: 'add' | 'remove' }>) => {
      if (action.payload.action === 'add') {
        state.contentMarkedForDeletion = { ...state.contentMarkedForDeletion, [action.payload.id]: true };
      } else {
        const newObject = { ...state.contentMarkedForDeletion };
        delete newObject[action.payload.id];
        state.contentMarkedForDeletion = newObject;
      }
    }
  ),
  updateContentMarkedForDeletionAll: create.reducer(
    (state, action: PayloadAction<{ action: 'add' | 'remove'; ids?: Array<string | number> }>) => {
      if (action.payload.action === 'add' && action.payload.ids) {
        const addedIds = action.payload.ids.reduce(
          (acc, id) => {
            acc[id] = true;
            return acc;
          },
          {} as Record<string | number, boolean>
        );
        state.contentMarkedForDeletion = { ...state.contentMarkedForDeletion, ...addedIds };
      } else {
        state.contentMarkedForDeletion = {};
      }
    }
  ),
  exitEditing: create.reducer((state) => {
    state.contentMarkedForDeletion = {};
  }),
  setSelectedFileType: create.reducer((state, action: PayloadAction<number>) => {
    state.selectedFileType = action.payload;
    state.pageNumber = 1;
    state.contentMarkedForDeletion = {};
  }),
  setContentTypeToAdd: create.reducer((state, action: PayloadAction<UPLOAD_TYPES | null>) => {
    state.contentTypeToAdd = action.payload;
  }),
  setPageNumber: create.reducer((state, action: PayloadAction<number>) => {
    state.pageNumber = action.payload;
  }),
  setProjectsFilter: create.reducer((state, action: PayloadAction<TReactSelectOption[]>) => {
    state.projectsFilter = action.payload;
  }),
  setProductAttributesFilter: create.reducer((state, action: PayloadAction<TReactSelectOption[]>) => {
    state.productAttributesFilter = action.payload;
  }),
  setCurrentContent: create.reducer((state, action: PayloadAction<(string | number)[]>) => {
    state.currentContent = action.payload;
  }),
  createUploadJob: create.reducer(
    (state, action: PayloadAction<{ onStarted?: () => void; onComplete: (video: StaticVideo) => void }>) => {
      state.uploadJobs = [
        ...state.uploadJobs,
        {
          fileSelected: false,
          uploading: false,
          onStarted: action.payload.onStarted,
          onComplete: action.payload.onComplete,
          uuid: uuid(),
        },
      ];
    }
  ),
  updateUploadJob: create.reducer((state, action: PayloadAction<UploadJob>) => {
    state.uploadJobs = state.uploadJobs.map((job) => (job.uuid === action.payload.uuid ? action.payload : job));
  }),
  finishUploadJob: create.reducer((state, action: PayloadAction<string>) => {
    state.uploadJobs = state.uploadJobs.filter((job) => job.uuid !== action.payload);
  }),
  clearUploadedVideos: create.reducer((state) => {
    state.uploadedVideos = null;
  }),
  clearRecordings: create.reducer((state) => {
    state.recordings = null;
  }),
  clearUploadJobs: create.reducer((state) => {
    state.uploadJobs = [];
  }),
  loadTemplates: create.asyncThunk(
    async (channel: number) => {
      return await GetTemplates(channel);
    },
    {
      pending: (state) => {
        state.loadingContent = true;
      },
      fulfilled: (state, action) => {
        state.templates = action.payload;
      },
      settled: (state) => {
        state.loadingContent = false;
      },
    }
  ),
  loadFonts: create.asyncThunk(
    async (channel: number) => {
      return await GetFonts(channel);
    },
    {
      pending: (state) => {
        state.loadingContent = true;
      },
      fulfilled: (state, action) => {
        state.fonts = action.payload;
      },
      settled: (state) => {
        state.loadingContent = false;
      },
    }
  ),
  loadThemePacks: create.asyncThunk(
    async (channel: number) => {
      return await GetThemePacks(channel);
    },
    {
      rejected: (state, action) => {
        console.error(action.error.stack);
      },
      pending: (state) => {
        state.loadingContent = true;
      },
      fulfilled: (state, action) => {
        state.themePacks = action.payload;
      },
      settled: (state) => {
        state.loadingContent = false;
      },
    }
  ),
  deleteDocuments: create.asyncThunk(
    async (channel: number, { getState }) => {
      const state = (getState() as RootState).content as ContentState;
      return await DeleteDocuments(channel, Object.keys(state.contentMarkedForDeletion).map(Number));
    },
    {
      fulfilled: (state, action) => {
        if (action.payload.cannotDelete.length) {
          const cannotDeleteSet = new Set(action.payload.cannotDelete || []);

          const errorMessage =
            state.documents
              ?.filter((doc) => cannotDeleteSet.has(doc.document))
              .map((doc) => doc.display_name)
              .join(', ') || '';
          cannotDeleteToast(errorMessage);
        }

        if (state.documents) {
          state.documents = state.documents.filter((doc) => !action.payload.deletedDocuments.includes(doc.document));
          state.contentMarkedForDeletion = {};
        }
      },
    }
  ),
  deleteRecording: create.asyncThunk(
    async (channel: number, { getState }) => {
      const state = (getState() as RootState).content as ContentState;
      return await DeleteVideos(channel, Object.keys(state.contentMarkedForDeletion));
    },
    {
      fulfilled: (state, action) => {
        if (action.payload.cannotDelete.length) {
          const cannotDeleteSet = new Set(action.payload.cannotDelete || []);

          const errorMessage =
            state.recordings
              ?.filter((video) => cannotDeleteSet.has(video.uuid))
              .map((video) => video.name)
              .join(', ') || '';
          cannotDeleteToast(errorMessage);
        }

        if (state.recordings) {
          state.recordings = state.recordings.filter((vid) => !action.payload.videoIDs.includes(vid.uuid));
          state.contentMarkedForDeletion = {};
        }
      },
    }
  ),
  deleteUploadedVideo: create.asyncThunk(
    async (channel: number, { getState }) => {
      const state = (getState() as RootState).content as ContentState;
      return await DeleteVideos(channel, Object.keys(state.contentMarkedForDeletion));
    },
    {
      fulfilled: (state, action) => {
        if (action.payload.cannotDelete.length) {
          const cannotDeleteSet = new Set(action.payload.cannotDelete || []);

          const errorMessage =
            state.uploadedVideos
              ?.filter((video) => cannotDeleteSet.has(video.uuid))
              .map((video) => video.name)
              .join(', ') || '';
          cannotDeleteToast(errorMessage);
        }

        if (state.uploadedVideos) {
          state.uploadedVideos = state.uploadedVideos.filter(
            (uploadedVid) => !action.payload.videoIDs.includes(uploadedVid.uuid)
          );
          state.contentMarkedForDeletion = {};
        }
      },
    }
  ),
  deleteThemePack: create.asyncThunk(
    async (channel: number, { getState }) => {
      const state = (getState() as RootState).content as ContentState;
      return await DeleteColors(Object.keys(state.contentMarkedForDeletion).map(Number), channel);
    },
    {
      fulfilled: (state, action) => {
        if (action.payload.cannotDelete.length) {
          const cannotDeleteSet = new Set(action.payload.cannotDelete || []);

          const errorMessage =
            state.themePacks
              ?.filter((themePack) => cannotDeleteSet.has(themePack.color_pack))
              .map((themePack) => themePack.title)
              .join(', ') || '';
          cannotDeleteToast(errorMessage);
        }

        if (state.themePacks) {
          state.themePacks = state.themePacks.filter(
            (themePack) => !action.payload.deletedColors.includes(themePack.color_pack)
          );
          state.contentMarkedForDeletion = {};
        }
      },
    }
  ),
});
